import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import type {
  ContentColumns,
  ContentTable,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from "pdfmake/interfaces";
import { DEFAULT_EXPORT_FILE_NAME } from "./constants";
import { formatExportValue } from "./export-utils";
import type {
  ExportTableCell,
  ExportTableModel,
  PdfExportOptions,
  PdfExportResult,
  PdfTextTableCell,
} from "./export.types";

const DEFAULT_MIME_TYPE = "application/pdf";
const DEFAULT_PAGE_ORIENTATION = "landscape";
const DEFAULT_PAGE_SIZE = "A4";
const DEFAULT_APPLICATION_TITLE = "Cayosoft Administrator";

let fontsInitialized = false;

const initializePdfMakeFonts = (): void => {
  if (fontsInitialized) {
    return;
  }

  pdfMake.addVirtualFileSystem(pdfFonts);
  fontsInitialized = true;
};

const REPORT_THEME = {
  headerBgColor: "#FF801E",
  rowColor: "white",
  alternateRowColor: "#F2F2F2",
  tableHeaderBgColor: "#00B0F0",
  tableLineColor: "white",
  headerTextColor: "white",
  tableBodyTextColor: "black",
  reportTitleFontSize: 14,
  defaultFontSize: 9,
} as const;

export class PdfExportService {
  serialize(table: ExportTableModel, options: PdfExportOptions = {}): PdfExportResult {
    return {
      definition: this.buildDefinition(table, options),
      fileName: options.fileName ?? `${DEFAULT_EXPORT_FILE_NAME}.pdf`,
      mimeType: DEFAULT_MIME_TYPE,
    };
  }

  async download(table: ExportTableModel, options: PdfExportOptions = {}): Promise<void> {
    initializePdfMakeFonts();

    const result = this.serialize(table, options);
    await pdfMake.createPdf(result.definition).download(result.fileName);
  }

  buildDefinition(table: ExportTableModel, options: PdfExportOptions = {}): TDocumentDefinitions {
    const headerRow: TableCell[] = table.columns.map((column) => ({
      text: column.headerText,
      style: "tableHeader",
    }));
    const rows: TableCell[][] = table.rows.map((row) =>
      row.cells.map((cell) => this._formatCellValue(cell))
    );
    const body: TableCell[][] = [headerRow, ...rows];

    const definition: TDocumentDefinitions = {
      pageOrientation: options.pageOrientation ?? DEFAULT_PAGE_ORIENTATION,
      pageSize: options.pageSize ?? DEFAULT_PAGE_SIZE,
      pageMargins: [40, 50, 40, 40],
      content: [
        {
          style: "tableStyle",
          table: {
            headerRows: 1,
            widths: table.columns.map((column) => column.width ?? "*"),
            body,
          },
        },
      ],
      styles: {
        tableStyle: {
          margin: [0, 0, 0, 0],
        },
        tableHeader: {
          fontSize: 12,
          bold: true,
          color: REPORT_THEME.headerTextColor,
          fillColor: REPORT_THEME.tableHeaderBgColor,
        },
      },
      defaultStyle: {
        fontSize: REPORT_THEME.defaultFontSize,
      },
    };

    this._buildHeader(
      definition,
      options.reportTitle ?? table.title,
      options.generatedAt ?? table.generatedAt
    );
    this._buildFooter(definition, options.applicationTitle ?? DEFAULT_APPLICATION_TITLE);
    this._buildTable(definition);

    return definition;
  }

  private _formatCellValue(cell: ExportTableCell): string {
    const rawValue = cell.formattedValue ?? cell.value;

    if (rawValue === null || rawValue === undefined) {
      return "";
    }

    return formatExportValue(rawValue);
  }

  private _buildHeader(
    docDefinition: TDocumentDefinitions,
    title: string | undefined,
    generatedAt: Date
  ): void {
    docDefinition.header = () => {
      const reportTitle = (title || "Report").replace(/\//g, " > ");
      const generatedAtText = generatedAt.toLocaleString("en-US").replace(/\u200E/g, "");

      return {
        margin: [0, 0, 0, 0],
        columns: [
          {
            table: {
              widths: [25, "*", "auto", 25],
              body: [
                [
                  {
                    text: " ",
                    fillColor: REPORT_THEME.headerBgColor,
                    border: [false, false, false, false],
                  },
                  {
                    text: reportTitle,
                    color: REPORT_THEME.headerTextColor,
                    fillColor: REPORT_THEME.headerBgColor,
                    margin: [0, 0, 0, 0],
                    border: [true, false, false, true],
                    fontSize: REPORT_THEME.reportTitleFontSize,
                  },
                  {
                    text: generatedAtText,
                    color: REPORT_THEME.headerTextColor,
                    fillColor: REPORT_THEME.headerBgColor,
                    alignment: "left",
                    border: [true, false, false, true],
                    margin: [0, 0, 0, 0],
                    fontSize: REPORT_THEME.reportTitleFontSize,
                  },
                  {
                    text: " ",
                    fillColor: REPORT_THEME.headerBgColor,
                    border: [true, false, false, true],
                  },
                ],
              ],
            },
            layout: {
              defaultBorder: false,
              fillColor: REPORT_THEME.headerBgColor,
              paddingBottom: () => 10,
              paddingTop: () => 10,
              vLineColor: () => REPORT_THEME.headerBgColor,
              vLineWidth: () => 2,
              hLineWidth: () => 0,
            },
          },
        ],
      } satisfies ContentColumns;
    };
  }

  private _buildFooter(docDefinition: TDocumentDefinitions, applicationTitle: string): void {
    docDefinition.footer = (currentPage: number, pageCount: number) => ({
      table: {
        widths: [25, "*", "auto", 25],
        body: [
          [
            {
              text: " ",
              border: [false, false, false, false],
            },
            {
              text: applicationTitle,
              color: REPORT_THEME.headerBgColor,
              border: [false, true, false, false],
              fontSize: 10,
            },
            {
              text: `${currentPage} / ${pageCount}`,
              color: REPORT_THEME.tableBodyTextColor,
              alignment: "left",
              border: [false, true, false, false],
              margin: [0, 4, 0, 0],
            },
            {
              text: " ",
            },
          ],
        ],
      },
      layout: {
        defaultBorder: false,
        paddingBottom: () => 10,
        paddingTop: () => 10,
        hLineColor: () => REPORT_THEME.tableHeaderBgColor,
        vLineWidth: () => 0,
        hLineWidth: () => 0.1,
      },
    });
  }

  private _buildTable(docDefinition: TDocumentDefinitions) {
    const tableContent = this._getMainTableContent(docDefinition.content);

    if (!tableContent) {
      return;
    }

    this._alignTableWidths(tableContent);

    this._buildTableBody(tableContent.table.body);

    const tableLayout: CustomTableLayout = {
      defaultBorder: false,
      vLineWidth: () => 3,
      hLineWidth: () => 0,
      vLineColor: () => REPORT_THEME.tableLineColor,
      paddingBottom: () => 4,
      paddingTop: () => 4,
    };
    tableContent.layout = tableLayout;
  }

  private _buildTableBody(body: TableCell[][]): void {
    const [headerRow, ...dataRows] = body;

    if (headerRow) {
      this._buildHeaderRow(headerRow);
    }

    dataRows.forEach((row, rowIndex) => {
      const fillColor = rowIndex % 2 === 0 ? REPORT_THEME.rowColor : REPORT_THEME.alternateRowColor;

      this._buildDataRow(row, fillColor);
    });
  }

  private _buildHeaderRow(row: TableCell[]): void {
    row.forEach((cell, cellIndex) => {
      row[cellIndex] = this._buildHeaderCell(cell, cellIndex === row.length - 1);
    });
  }

  private _buildHeaderCell(cell: TableCell, isLastCell: boolean): TableCell {
    const headerCell = this._normalizeTableCell(cell);

    headerCell.noWrap = true;
    headerCell.alignment = "center";
    headerCell.border = [false, false, !isLastCell, false];

    return headerCell;
  }

  private _buildDataRow(row: TableCell[], fillColor: string): void {
    row.forEach((cell, cellIndex) => {
      row[cellIndex] = this._buildDataCell(cell, fillColor, cellIndex === row.length - 1);
    });
  }

  private _buildDataCell(cell: TableCell, fillColor: string, isLastCell: boolean): TableCell {
    const baseCell = this._normalizeTableCell(cell);

    return {
      ...baseCell,
      text: baseCell.text,
      fillColor,
      color: REPORT_THEME.tableBodyTextColor,
      alignment: "center",
      border: [false, false, !isLastCell, false],
    };
  }

  private _normalizeTableCell(cell: TableCell): PdfTextTableCell {
    if (typeof cell === "string" || typeof cell === "number") {
      return { text: `${cell}` };
    }

    if (this._isPdfTextTableCell(cell)) {
      const normalizedCell: PdfTextTableCell = {
        text: typeof cell.text === "string" ? cell.text : "",
        style: cell.style,
        color: cell.color,
        fillColor: cell.fillColor,
        margin: cell.margin,
        fontSize: cell.fontSize,
        noWrap: cell.noWrap,
        alignment: cell.alignment,
        border: cell.border,
      };
      return normalizedCell;
    }

    return { text: "" };
  }

  private _alignTableWidths(tableContent: ContentTable): void {
    const tableWidths = Array.isArray(tableContent.table.widths)
      ? tableContent.table.widths
      : undefined;

    if (!tableWidths?.length) {
      return;
    }

    const widths = tableWidths.filter((width): width is number => typeof width === "number");
    const totalWidth = widths.reduce((totalWidth, width) => totalWidth + width, 0);

    if (!totalWidth) {
      return;
    }

    tableContent.table.widths = tableWidths.map((width) =>
      typeof width === "number" ? `${(width / totalWidth) * 100}%` : width
    );
  }

  private _getMainTableContent(content: TDocumentDefinitions["content"]): ContentTable | undefined {
    if (!Array.isArray(content)) {
      return undefined;
    }

    const [mainContent] = content;

    return this._isContentTable(mainContent) ? mainContent : undefined;
  }

  private _isContentTable(content: unknown): content is ContentTable {
    return (
      typeof content === "object" &&
      content !== null &&
      !Array.isArray(content) &&
      "table" in content
    );
  }

  private _isPdfTextTableCell(cell: TableCell): cell is PdfTextTableCell {
    return typeof cell === "object" && cell !== null && !Array.isArray(cell) && "text" in cell;
  }
}

const pdfExportService = new PdfExportService();

export { pdfExportService };
